import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { BookOpenCheck, Loader2, LogOut } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { authClient } from "#/lib/auth-client";
import { EMAIL_PASSWORD_ENABLED } from "#/lib/feature-flags";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in | Bookling" },
      {
        name: "description",
        content: "Sign in to contribute podcast book mentions to Bookling.",
      },
      {
        name: "robots",
        content: "noindex, nofollow",
      },
    ],
  }),
  component: AuthPage,
});

const authSchema = z.object({
  name: z.string().trim().max(120),
  email: z.string().trim().email("Enter a valid email address."),
  password: z.string().min(8, "Use at least 8 characters."),
});

type AuthValues = z.infer<typeof authSchema>;

function GoogleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

function AuthPage() {
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const [mode, setMode] = useState<"sign-in" | "sign-up">("sign-in");
  const [error, setError] = useState<string | null>(null);

  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleSubmitting(true);
    const result = await authClient.signIn.social({
      provider: "google",
      callbackURL: "/",
    });
    if (result.error) {
      setError(result.error.message || "Google sign-in failed.");
      setIsGoogleSubmitting(false);
    }
  };

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
    } satisfies AuthValues,
    validators: {
      onSubmit: authSchema,
    },
    onSubmit: async ({ value }) => {
      setError(null);

      const result =
        mode === "sign-up"
          ? await authClient.signUp.email({
              email: value.email,
              password: value.password,
              name: value.name || value.email,
            })
          : await authClient.signIn.email({
              email: value.email,
              password: value.password,
            });

      if (result.error) {
        setError(result.error.message || "Authentication failed.");
        return;
      }

      await navigate({ to: "/" });
    },
  });

  if (isPending) {
    return (
      <main className="auth-page">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      </main>
    );
  }

  if (session?.user) {
    return (
      <main className="auth-page">
        <section className="auth-panel">
          <p className="eyebrow">Signed in</p>
          <h1>{session.user.name || session.user.email}</h1>
          <p>{session.user.email}</p>
          <button
            className="button-secondary"
            type="button"
            onClick={() => {
              void authClient.signOut({
                fetchOptions: {
                  onSuccess: () => {
                    void navigate({ to: "/" });
                  },
                },
              });
            }}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div className="auth-mark">
          <BookOpenCheck className="h-5 w-5" aria-hidden="true" />
        </div>
        <p className="eyebrow">Bookling contributors</p>
        <h1>{mode === "sign-up" ? "Create access" : "Welcome back"}</h1>
        <p>
          {EMAIL_PASSWORD_ENABLED
            ? "Use Google or email + password to submit, correct, and enrich podcast book mentions."
            : "Sign in with Google to submit, correct, and enrich podcast book mentions."}
        </p>

        <button
          className="button-secondary google-button"
          type="button"
          onClick={() => void handleGoogleSignIn()}
          disabled={isGoogleSubmitting}
        >
          {isGoogleSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <GoogleIcon />
          )}
          Continue with Google
        </button>

        {!EMAIL_PASSWORD_ENABLED && error ? <p className="form-error">{error}</p> : null}

        {EMAIL_PASSWORD_ENABLED ? (
          <>
            <div className="auth-divider" role="separator">
              <span>or</span>
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit();
              }}
              className="auth-form"
            >
              {mode === "sign-up" ? (
                <form.Field name="name">
                  {(field) => (
                    <label className="field">
                      <span>Name</span>
                      <input
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(event) => field.handleChange(event.target.value)}
                        autoComplete="name"
                      />
                    </label>
                  )}
                </form.Field>
              ) : null}

              <form.Field name="email">
                {(field) => (
                  <label className="field">
                    <span>Email</span>
                    <input
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) => field.handleChange(event.target.value)}
                      type="email"
                      autoComplete="email"
                    />
                  </label>
                )}
              </form.Field>

              <form.Field name="password">
                {(field) => (
                  <label className="field">
                    <span>Password</span>
                    <input
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) => field.handleChange(event.target.value)}
                      type="password"
                      autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
                    />
                  </label>
                )}
              </form.Field>

              {error ? <p className="form-error">{error}</p> : null}

              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <button
                    className="button-primary"
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <BookOpenCheck className="h-4 w-4" aria-hidden="true" />
                    )}
                    {mode === "sign-up" ? "Create account" : "Sign in"}
                  </button>
                )}
              </form.Subscribe>
            </form>

            <button
              className="mode-switch"
              type="button"
              onClick={() => {
                setError(null);
                setMode(mode === "sign-in" ? "sign-up" : "sign-in");
              }}
            >
              {mode === "sign-in" ? "Need an account? Create one" : "Already have access? Sign in"}
            </button>
          </>
        ) : null}
      </section>
    </main>
  );
}
